/* eslint-disable @typescript-eslint/no-explicit-any */
import { XMLParser } from 'fast-xml-parser';
import { ProductId } from './newBackgroundCheckUtils';

type Subject = {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    dob?: number;
    ssn: string;
};

type DateEntity = {
    year: number;
    month: number;
    day: number;
};

type SsnValidationXml = {
    ssn: number;
    issuestate: string;
    firstissuedyear: number;
    lastissuedyear: number;
    ssnvalid: string;
    message: string;
    death: string;
};

export type ParsedSsnValidation = {
    ssn?: string;
    stateIssuedBy: string;
    firstIssuedYear: number;
    lastIssuedYear: number;
    ssnValid: string;
    message: string;
    death: string;
};

type SsnSubjectXML = {
    age?: number;
    city?: string;
    county?: string;
    'date-first'?: DateEntity;
    'date-last'?: DateEntity;
    dob?: string;
    gender?: string;
    'name-first'?: string;
    'name-last'?: string;
    'name-middle'?: string;
    'phone_name-dual'?: string;
    phone_phone10?: number;
    phone_pubnonpub?: string;
    ssn?: string;
    state?: string;
    'street-name'?: string;
    'street-number'?: number;
    'street-pre-direction'?: string;
    'street-suffix'?: string;
    timezone?: string;
    type?: string;
    'unit-designation'?: string;
    'unit-number'?: string;
    verified?: string;
    zip?: number;
};

export type ParsedSsnSubject = {
    age?: number;
    city?: string;
    county?: string;
    dateFirst?: DateEntity;
    dateLast?: DateEntity;
    dob?: DateEntity;
    gender?: string;
    nameFirst?: string;
    nameLast?: string;
    nameMiddle?: string;
    phoneNameDual?: string;
    phonePhone10: number;
    phonePublicOrPrivate?: string;
    ssn?: string;
    state?: string;
    streetName?: string;
    streetNumber: number;
    streetPreDirection?: string;
    streetSuffix?: string;
    timezone?: string;
    type?: string;
    unitDesignation?: string;
    unitNumber?: string;
    verified?: string;
    zip?: number;
};

type Details = {
    lastName?: string;
    firstName?: string;
    suffix?: string;
    dob?: string;
    ssn?: string;
    sex?: string;
    race?: string;
    heightFeet?: string;
    heightInches?: string;
    eyes?: string;
    state?: string;
    county?: string;
    recordType?: string;
    importDate?: string;
    matchCriteria?: string;
    personId?: number;
};

export type ParsedAddress = {
    addressNo?: string;
    streetNo?: string;
    streetName?: string;
    aptNo?: string;
    city?: string;
    state?: string;
    zip?: string;
    importDate?: string;
};

type Note = {
    tableId?: string;
    noteNo?: string;
    noteType?: string;
    note?: string;
    noteDate?: string;
};

type Crime = {
    crimeNo?: string;
    sequenceNo?: string;
    crimeCounts?: string;
    chargeDesc?: string;
    chargeClass?: string;
    acc?: string;
    offenceDate?: string;
    importDate?: string;
};

type Alias = {
    akaCount?: string;
    akaLastName?: string;
    akaFirstName?: string;
    akaDob?: string;
    importDate?: string;
};

export type ParsedCharge = {
    caseNo?: string;
    chargeNo?: number;
    chargeCode?: string;
    chargeDesc?: string;
    dispoDate?: string;
    plea?: string;
    disposition?: string;
    offenseLevel?: string;
    importDate?: string;
};

export type ParsedCases = {
    caseNo?: string;
    caseType?: string;
    courtId?: string;
    arrestDate?: string;
    bookingDate?: string;
    fileDate?: string;
    arrestingAgency?: string;
    caseNotes?: string;
    sentence?: string;
    importDate?: string;
    charges: ParsedCharge[];
};

type SexOffenses = {
    chargeNo?: string;
    chargeDesc?: string;
    convictionDate?: string;
    lastRegisteredDate?: string;
    importDate?: string;
};

type Inmate = {
    inmateId?: string;
    sequenceNo?: string;
    admissionDate?: string;
    releaseDate?: string;
    releaseReason?: string;
    sentMax?: string;
    sentMonth?: string;
    sentYear?: string;
    inmateStatus?: string;
    inmateNote?: string;
    importDate?: string;
};

type Probation = {
    probationNo?: string;
    circuit?: string;
    beginDate?: string;
    endDate?: string;
    probateDay?: string;
    probateMonth?: string;
    probateYear?: string;
    probateDesc?: string;
    importDate?: string;
};

type Parole = {
    paroleNo?: string;
    paroleAgency?: string;
    beginDate?: string;
    endDate?: string;
    paroleDesc?: string;
    importDate?: string;
};

export interface ParsedPerson extends Details {
    address?: ParsedAddress;
    note?: Note;
    crime?: Crime;
    alias?: Alias;
    cases?: ParsedCases[];
    sexOffences?: SexOffenses;
    inmate?: Inmate;
    probation?: Probation;
    parole?: Parole;
}

export interface ParsedBackgroundCheck {
    subject: Subject;
    persons?: ParsedPerson[];
    ssnSubjects?: ParsedSsnSubject[];
    ssnValidationData?: ParsedSsnValidation;
}

export function parseBackgroundCheck(xml: string): ParsedBackgroundCheck {
    const options = {
        // transformTagName: (tagName: string) => tagName.toLowerCase(),
        isArray: (name: string) => name === 'Charges',
    };
    const parser = new XMLParser(options);
    const result = parser.parse(xml);

    const xmlData = result?.xml?.URLSearchResponse;

    if (!xmlData) {
        throw new Error('No XML data available.');
    }

    try {
        const subject = getSubject(xmlData);
        const ssnSubjects = getSsnSubjects(xmlData);
        const criminalRecords = getCriminalRecords(xmlData);
        const persons = getCriminalPersons(criminalRecords);
        const ssnValidationData = getSsnValidationData(xmlData);

        return {
            subject,
            ...(ssnSubjects.length && { ssnSubjects: [...ssnSubjects] }),
            ...(persons.length && { persons: [...persons] }),
            ...(ssnValidationData && { ssnValidationData }),
        };
    } catch (e: any) {
        throw new Error('There was something wrong with the XML parse.');
    }
}

function getSsnValidationData(xmlData: any): ParsedSsnValidation | undefined {
    const components = xmlData.ComponentsList?.Component || [];
    const arrayOfComponents = Array.isArray(components) ? components : [...(components ? [components] : [])];
    const ssnComponent = arrayOfComponents.find((component: any) => {
        const { ProductId: productId } = component;

        return productId === ProductId.SsnVerification;
    });
    const ssnValidationRecord = ssnComponent?.ResultXML.SSN.response?.ssnvalidation as SsnValidationXml;

    if (!ssnValidationRecord) {
        return;
    }

    const {
        ssn,
        message,
        death,
        ssnvalid: ssnValid,
        firstissuedyear: firstIssuedYear,
        lastissuedyear: lastIssuedYear,
        issuestate: stateIssuedBy,
    } = ssnValidationRecord;

    return {
        ssn: getCleanedSsn(ssn),
        stateIssuedBy,
        firstIssuedYear,
        lastIssuedYear,
        ssnValid,
        message,
        death,
    };
}

function getSubject(xmlData: any): Subject {
    if (Array.isArray(xmlData.Subject)) {
        throw new Error('More than one element named Subject found.');
    }

    const {
        Subject: { DOB: dob, FirstName: firstName, MiddleName: middleName, LastName: lastName, SSN: ssn },
    } = xmlData;

    return {
        firstName,
        middleName,
        lastName,
        dob,
        ssn: getCleanedSsn(ssn),
    };
}

function getSsnSubjects(xmlData: any): ParsedSsnSubject[] {
    const components = xmlData.ComponentsList?.Component || [];
    const arrayOfComponents = Array.isArray(components) ? components : [...(components ? [components] : [])];
    const ssnComponent = arrayOfComponents.find((component: any) => {
        const { ResultXML } = component;

        return ResultXML.SSN;
    });
    const ssnRecords = ssnComponent?.ResultXML.SSN.response?.record || [];
    const arrayOfSsnRecords = Array.isArray(ssnRecords) ? ssnRecords : ([...(ssnRecords ? [ssnRecords] : [])] as SsnSubjectXML[]);

    return arrayOfSsnRecords.map((record) => {
        const {
            age,
            city,
            county,
            'date-first': dateFirst,
            'date-last': dateLast,
            dob,
            gender,
            'name-first': nameFirst,
            'name-last': nameLast,
            'name-middle': nameMiddle,
            'phone_name-dual': phoneNameDual,
            phone_phone10: phonePhone10,
            phone_pubnonpub: phonePubnonpub,
            ssn,
            state,
            'street-name': streetName,
            'street-number': streetNumber,
            'street-pre-direction': streetPreDirection,
            'street-suffix': streetSuffix,
            timezone,
            type,
            'unit-designation': unitDesignation,
            'unit-number': unitNumber,
            verified,
            zip,
        } = record || {};

        return (
            record && {
                ...(age && { age: age }),
                ...(city && { city: city }),
                ...(county && { county: county }),
                ...(dateFirst && { dateFirst: dateFirst }),
                ...(dateLast && { dateLast: dateLast }),
                ...(dob && { dob: dob }),
                ...(gender && { gender: gender }),
                ...(nameFirst && { nameFirst: nameFirst }),
                ...(nameLast && { nameLast: nameLast }),
                ...(nameMiddle && { nameMiddle: nameMiddle }),
                ...(phoneNameDual && { phoneNameDual: phoneNameDual }),
                ...(phonePhone10 && { phonePhone10: phonePhone10 }),
                ...(phonePubnonpub && { phonePubnonpub: phonePubnonpub }),
                ...(ssn && { ssn: getCleanedSsn(ssn) }),
                ...(state && { state: state }),
                ...(streetName && { streetName: streetName }),
                ...(streetNumber && { streetNumber: streetNumber }),
                ...(streetPreDirection && { streetPreDirection: streetPreDirection }),
                ...(streetSuffix && { streetSuffix: streetSuffix }),
                ...(timezone && { timezone: timezone }),
                ...(type && { type: type }),
                ...(unitDesignation && { unitDesignation: unitDesignation }),
                ...(unitNumber && { unitNumber: unitNumber }),
                ...(verified && { verified: verified }),
                ...(zip && { zip: zip }),
            }
        );
    });
}

function getCriminalRecords(xmlData: any) {
    const components = xmlData.ComponentsList?.Component || [];
    const arrayOfComponents = Array.isArray(components) ? components : [components];

    return arrayOfComponents.filter((component: any) => {
        const { ResultXML, RecordFound } = component;

        return ResultXML.Criminal && RecordFound === 'True';
    });
}

function getCriminalPersons(criminalRecords: any = []): ParsedPerson[] {
    const criminalPersons = criminalRecords.reduce((prevRecord: any[], record: any) => {
        const person = record.ResultXML.Criminal?.Person;
        if (Array.isArray(person)) return [...prevRecord, ...person];
        if (person) return [...prevRecord, person];
        return prevRecord;
    }, []);

    return getCriminalPersonsResolved(criminalPersons);
}

function getCriminalPersonsResolved(criminalPersons: any): ParsedPerson[] {
    return criminalPersons.map((person: any) => {
        const { Address, Note, Crime, Alias, Cases, SexOffenses, Inmate, Probation, Parole } = person;
        const details = getDetails(person);
        const address = getAddress(Address);
        const note = getNote(Note);
        const crime = getCrime(Crime);
        const alias = getAlias(Alias);
        const cases = getCases(Cases);
        const sexOffences = getSexOffenses(SexOffenses);
        const inmate = getInmate(Inmate);
        const probation = getProbation(Probation);
        const parole = getParole(Parole);

        return {
            ...details,
            ...(address && { address }),
            ...(note && { note }),
            ...(crime && { crime }),
            ...(alias && { alias }),
            ...(cases && { cases }),
            ...(sexOffences && { sexOffences }),
            ...(inmate && { inmate }),
            ...(probation && { probation }),
            ...(parole && { parole }),
        };
    });
}

function getDetails(person: any): Details {
    const {
        LastName: lastName,
        FirstName: firstName,
        Suffix: suffix,
        DOB: dob,
        SSN: ssn,
        Sex: sex,
        Race: race,
        HeightFeet: heightFeet,
        HeightInches: heightInches,
        Eyes: eyes,
        State: state,
        County: county,
        RecordType: recordType,
        ImportDate: importDate,
        MatchCriteria: matchCriteria,
        PersonID: personId,
    } = person || {};

    return (
        person && {
            ...(lastName && { lastName: lastName }),
            ...(firstName && { firstName: firstName }),
            ...(suffix && { suffix: suffix }),
            ...(dob && { dob: dob }),
            ...(ssn && { ssn: getCleanedSsn(ssn) }),
            ...(sex && { sex: sex }),
            ...(race && { race: race }),
            ...(heightFeet && { heightFeet: heightFeet }),
            ...(heightInches && { heightInches: heightInches }),
            ...(eyes && { eyes: eyes }),
            ...(state && { state: state }),
            ...(county && { county: county }),
            ...(recordType && { recordType: recordType }),
            ...(importDate && { importDate: importDate }),
            ...(matchCriteria && { matchCriteria: matchCriteria }),
            ...(personId && { personId: personId }),
        }
    );
}

function getAddress(addressObj: any): ParsedAddress {
    const {
        AddressNo: addressNo,
        StreetNo: streetNo,
        StreetName: streetName,
        AptNo: aptNo,
        City: city,
        State: state,
        Zip: zip,
        ImportDate: importDate,
    } = addressObj || {};

    return (
        addressObj && {
            ...(addressNo && { addressNo: addressNo }),
            ...(streetNo && { streetNo: streetNo }),
            ...(streetName && { streetName: streetName }),
            ...(aptNo && { aptNo: aptNo }),
            ...(city && { city: city }),
            ...(state && { state: state }),
            ...(zip && { zip: zip }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getNote(noteObj: any): Note {
    const { TableID: tableId, NoteNo: noteNo, NoteType: noteType, Note: note, NoteDate: noteDate } = noteObj || {};

    return (
        noteObj && {
            ...(tableId && { tableId: tableId }),
            ...(noteNo && { noteNo: noteNo }),
            ...(noteType && { noteType: noteType }),
            ...(note && { note: note }),
            ...(noteDate && { noteDate: noteDate }),
        }
    );
}

function getCrime(crimeObj: any): Crime {
    const {
        CrimeNo: crimeNo,
        SequenceNo: sequenceNo,
        CrimeCounts: crimeCounts,
        ChargeDesc: chargeDesc,
        ChargeClass: chargeClass,
        ACC: acc,
        OffenceDate: offenceDate,
        ImportDate: importDate,
    } = crimeObj || {};

    return (
        crimeObj && {
            ...(crimeNo && { crimeNo: crimeNo }),
            ...(sequenceNo && { sequenceNo: sequenceNo }),
            ...(crimeCounts && { crimeCounts: crimeCounts }),
            ...(chargeDesc && { chargeDesc: chargeDesc }),
            ...(chargeClass && { chargeClass: chargeClass }),
            ...(acc && { acc: acc }),
            ...(offenceDate && { offenceDate: offenceDate }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getAlias(aliasObj: any): Alias {
    const {
        AKACount: akaCount,
        AKALastName: akaLastName,
        AKAFirstName: akaFirstName,
        AKADOB: akaDob,
        ImportDate: importDate,
    } = aliasObj || {};

    return (
        aliasObj && {
            ...(akaCount && { akaCount: akaCount }),
            ...(akaLastName && { akaLastName: akaLastName }),
            ...(akaFirstName && { akaFirstName: akaFirstName }),
            ...(akaDob && { akaDob: akaDob }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getCases(casesObj: any): ParsedCases[] {
    const casesArray = Array.isArray(casesObj) ? casesObj : [...(casesObj ? [casesObj] : [])];

    return casesArray.map((caseEntity) => {
        const {
            CaseNo: caseNo,
            CaseType: caseType,
            CourtID: courtId,
            ArrestDate: arrestDate,
            BookingDate: bookingDate,
            FileDate: fileDate,
            ArrestingAgency: arrestingAgency,
            CaseNotes: caseNotes,
            Sentence: sentence,
            ImportDate: importDate,
            Charges,
        } = caseEntity || {};

        const charges = getCharges(Charges);

        return (
            caseEntity && {
                ...(caseNo && { caseNo: caseNo }),
                ...(caseType && { caseType: caseType }),
                ...(courtId && { courtId: courtId }),
                ...(arrestDate && { arrestDate: arrestDate }),
                ...(bookingDate && { bookingDate: bookingDate }),
                ...(fileDate && { fileDate: fileDate }),
                ...(arrestingAgency && { arrestingAgency: arrestingAgency }),
                ...(caseNotes && { caseNotes: caseNotes }),
                ...(sentence && { sentence: sentence }),
                ...(importDate && { importDate: importDate }),
                ...(charges.length && { charges: [...charges] }),
            }
        );
    });
}

function getCharges(chargesObj: any = []): ParsedCharge[] {
    const chargesArray = Array.isArray(chargesObj) ? chargesObj : [...(chargesObj ? [chargesObj] : [])];

    return chargesArray.map((charge: any) => {
        const {
            CaseNo: caseNo,
            ChargeNo: chargeNo,
            ChargeCode: chargeCode,
            ChargeDesc: chargeDesc,
            DispoDate: dispoDate,
            Plea: plea,
            Disposition: disposition,
            OffenseLevel: offenseLevel,
            ImportDate: importDate,
        } = charge || {};

        return (
            charge && {
                ...(caseNo && { caseNo: caseNo }),
                ...(chargeNo && { chargeNo: chargeNo }),
                ...(chargeCode && { chargeCode: chargeCode }),
                ...(chargeDesc && { chargeDesc: chargeDesc }),
                ...(dispoDate && { dispoDate: dispoDate }),
                ...(plea && { plea: plea }),
                ...(disposition && { disposition: disposition }),
                ...(offenseLevel && { offenseLevel: offenseLevel }),
                ...(importDate && { importDate: importDate }),
            }
        );
    });
}

function getSexOffenses(sexOffensesObj: any): SexOffenses {
    const {
        ChargeNo: chargeNo,
        ChargeDesc: chargeDesc,
        ConvictionDate: convictionDate,
        LastRegisteredDate: lastRegisteredDate,
        ImportDate: importDate,
    } = sexOffensesObj || {};

    return (
        sexOffensesObj && {
            ...(chargeNo && { chargeNo: chargeNo }),
            ...(chargeDesc && { chargeDesc: chargeDesc }),
            ...(convictionDate && { convictionDate: convictionDate }),
            ...(lastRegisteredDate && { lastRegisteredDate: lastRegisteredDate }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getInmate(inmateObj: any): Inmate {
    const {
        InmateID: inmateId,
        SequenceNo: sequenceNo,
        AdmissionDate: admissionDate,
        ReleaseDate: releaseDate,
        ReleaseReason: releaseReason,
        SentMax: sentMax,
        SentMonth: sentMonth,
        SentYear: sentYear,
        InmateStatus: inmateStatus,
        InmateNote: inmateNote,
        ImportDate: importDate,
    } = inmateObj || {};

    return (
        inmateObj && {
            ...(inmateId && { inmateId: inmateId }),
            ...(sequenceNo && { sequenceNo: sequenceNo }),
            ...(admissionDate && { admissionDate: admissionDate }),
            ...(releaseDate && { releaseDate: releaseDate }),
            ...(releaseReason && { releaseReason: releaseReason }),
            ...(sentMax && { sentMax: sentMax }),
            ...(sentMonth && { sentMonth: sentMonth }),
            ...(sentYear && { sentYear: sentYear }),
            ...(inmateStatus && { inmateStatus: inmateStatus }),
            ...(inmateNote && { inmateNote: inmateNote }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getProbation(probationObj: any): Probation {
    const {
        ProbationNo: probationNo,
        Circuit: circuit,
        BeginDate: beginDate,
        EndDate: endDate,
        ProbateDay: probateDay,
        ProbateMonth: probateMonth,
        ProbateYear: probateYear,
        ProbateDesc: probateDesc,
        ImportDate: importDate,
    } = probationObj || {};

    return (
        probationObj && {
            ...(probationNo && { probationNo: probationNo }),
            ...(circuit && { circuit: circuit }),
            ...(beginDate && { beginDate: beginDate }),
            ...(endDate && { endDate: endDate }),
            ...(probateDay && { probateDay: probateDay }),
            ...(probateMonth && { probateMonth: probateMonth }),
            ...(probateYear && { probateYear: probateYear }),
            ...(probateDesc && { probateDesc: probateDesc }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

function getParole(paroleObj: any): Parole {
    const {
        ParoleNo: paroleNo,
        ParoleAgency: paroleAgency,
        BeginDate: beginDate,
        EndDate: endDate,
        ParoleDesc: paroleDesc,
        ImportDate: importDate,
    } = paroleObj || {};

    return (
        paroleObj && {
            ...(paroleNo && { paroleNo: paroleNo }),
            ...(paroleAgency && { paroleAgency: paroleAgency }),
            ...(beginDate && { beginDate: beginDate }),
            ...(endDate && { endDate: endDate }),
            ...(paroleDesc && { paroleDesc: paroleDesc }),
            ...(importDate && { importDate: importDate }),
        }
    );
}

export function getCleanedSsn(ssn?: number | string) {
    if (!ssn) {
        return 'No SSN';
    }

    const cleanedSsn = ssn
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-4);

    if (cleanedSsn.length !== 4) {
        return ssn.toString();
    }

    return `***-**-${cleanedSsn}`;
}

export function cleanResults(text: string): string {
    const data = text.replace(/(<SSN>)(.*?)(<\/SSN>)/g, (match, tag1, ssn, tag2) => {
        const cleanedSsn = getCleanedSsn(ssn);

        return `${tag1}${cleanedSsn}${tag2}`;
    });

    return data.replace(/(<ssn>)(.*?)(<\/ssn>)/g, (match, tag1, ssn, tag2) => {
        const cleanedSsn = getCleanedSsn(ssn);

        return `${tag1}${cleanedSsn}${tag2}`;
    });
}
